import { Check, ChevronDown, ChevronUp, FlaskConical, Pencil, Plus, Trash2, X } from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetFertilizerData } from "../../../../api/static-data";
import ConfirmModal from "../../../../components/confirmViewModal/ConfirmModal";
import { FormInputNumber } from "../../../../components/ui/Input/FormInputNumber/FormInputNumber";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { Button } from "../../../../components/ui/common/button";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { Form } from "../../../../components/ui/common/form";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../../../components/ui/common/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../components/ui/common/tooltip";
import { CULTIVATION_TYPE } from "../../../../config/constants";
import { errorView } from "../../../../helpers/ErrorHandler";
import { validateCultivationNumber } from "../../../../utils/utils";
import { useCultivarSectionAccordion } from "../hooks/useCultivarSectionAccordion";
import CultivarSaveReminder, { DELETE_CONFIRM_MESSAGES } from "./CultivarSaveReminder";

const Fertilizer = forwardRef(
  (
    {
      currentFormData,
      currentDataType,
      lockStatus = false,
      sectionsDisabled = false,
      accordionEnabledStatus = false
    }: any,
    ref
  ) => {
    const [isEditingEntry, setIsEditingEntry] = useState(false);
    const [formEntryKey, setFormEntryKey] = useState(0);
    const [fertilizerDropdownList, setFertilizerDropdownList] = useState<any>([]);
    const [fertilizerTableList, setFertilizerTableList] = useState<any>([]);
    const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
    const [deleteValue, setDeleteValue] = useState<any>({
      item: "",
      type: ""
    });

    const { data: fertilizerListData, isSuccess: isFertilizerListDataSuccess } =
      useGetFertilizerData();

    // sort fertilizer list
    const sortedFertilizerList = useMemo(() => {
      return [...(fertilizerListData ?? [])].sort((a: any, b: any) =>
        a.fertilizer_name.localeCompare(b.fertilizer_name)
      );
    }, [fertilizerListData]);

    const fertilizerTableListData = useCallback(() => {
      const currentFertilizerData =
        currentFormData?.cultivationField?.fertilizerList?.map((item: any) => {
          const fertilizer = sortedFertilizerList?.find((f: any) => {
            return parseInt(f.id) === parseInt(item.id);
          });
          return {
            ...item,
            fertilizer_name: fertilizer?.fertilizer_name ?? "",
            id: parseInt(item.id)
          };
        }) ?? [];
      setFertilizerTableList(currentFertilizerData);
    }, [currentFormData, sortedFertilizerList]);

    useEffect(() => {
      fertilizerTableListData();
    }, [currentFormData, sortedFertilizerList]);

    useEffect(() => {
      if (isFertilizerListDataSuccess) {
        setFertilizerDropdownList(sortedFertilizerList);
      }
    }, [sortedFertilizerList, isFertilizerListDataSuccess]);

    const emptyFormValues = useMemo(
      () => ({
        fertilizerQuantity: "",
        fertilizer: ""
      }),
      []
    );

    const form = useForm({
      defaultValues: emptyFormValues,
      mode: "all",
      reValidateMode: "onChange"
    });
    const {
      control,
      handleSubmit,
      reset,
      clearErrors,
      formState: { errors }
    } = form;

    const hasErrors = Object.keys(errors).length > 0;

    const { accordionEnabled, toggleAccordion, headerClassName } = useCultivarSectionAccordion(
      sectionsDisabled,
      accordionEnabledStatus,
      hasErrors
    );

    useImperativeHandle(ref, () => ({
      getFretilizerData: () => {
        return fertilizerTableList;
      }
    }));

    const resetFertilizerForm = useCallback(() => {
      clearErrors();
      reset(emptyFormValues);
      setIsEditingEntry(false);
      setFormEntryKey(key => key + 1);
    }, [clearErrors, emptyFormValues, reset]);

    const addFertilizer = handleSubmit(formValues => {
      let updatedFertilizerDropdownList = [...(fertilizerDropdownList ?? [])];
      let updatedFertilizerTableList = [...(fertilizerTableList ?? [])];
      const selectedFertilizerId = formValues.fertilizer;

      if (updatedFertilizerDropdownList.length === 0 || !selectedFertilizerId) {
        errorView("Select fertilizer");
        return;
      }

      const doesFertilizerExists = updatedFertilizerTableList.find(
        item => parseInt(item.id) === parseInt(selectedFertilizerId)
      );
      if (doesFertilizerExists) {
        updatedFertilizerTableList = updatedFertilizerTableList.map(p => {
          if (parseInt(p.id) === parseInt(selectedFertilizerId)) {
            p.fertilizerQuantity = formValues.fertilizerQuantity;
          }
          return p;
        });
      } else {
        const fertilizerFromList = updatedFertilizerDropdownList.find(
          item => String(item.id) === String(selectedFertilizerId)
        );
        if (fertilizerFromList) {
          updatedFertilizerTableList.push({
            ...fertilizerFromList,
            fertilizerQuantity: formValues.fertilizerQuantity
          });
          updatedFertilizerDropdownList = updatedFertilizerDropdownList.filter(item => {
            return String(item.id) !== String(selectedFertilizerId);
          });
        }
      }
      setFertilizerTableList(updatedFertilizerTableList);
      setFertilizerDropdownList(updatedFertilizerDropdownList);
      resetFertilizerForm();
    });

    const deleteFertilizer = useCallback(() => {
      setFertilizerTableList((prevList: any[]) =>
        prevList.filter((_, index: number) => index !== deleteValue.index)
      );
      setVisibleDeleteModal(false);
      setDeleteValue({});
    }, [deleteValue]);

    const deleteFertilizerData = (item: any, index: number) => {
      setVisibleDeleteModal(true);
      setDeleteValue({
        type: "Fertilizer",
        item: item.fertilizer_name ?? "",
        index: index
      });
    };

    const onEditFertilizer = (item: any) => {
      setIsEditingEntry(true);
      setFertilizerDropdownList((prevList: any) => [...(prevList ?? []), item]);
      reset({
        fertilizerQuantity: item?.fertilizerQuantity?.toString() ?? "",
        fertilizer: item.id.toString()
      });
      setFormEntryKey(key => key + 1);
    };

    const handleClick = toggleAccordion;

    const editFertilizer = (item: any) => {
      onEditFertilizer(item);
    };

    return currentDataType === CULTIVATION_TYPE.Supplier ? (
      <Card className="tw-border-border tw-p-0 tw-gap-0">
        <CardHeader className={headerClassName} onClick={handleClick}>
          <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
            <div className="tw-flex tw-items-center tw-gap-2">
              <FlaskConical className="tw-h-4 tw-w-4 tw-text-primary" />
              <span className="tw-text-sm tw-font-semibold tw-text-foreground">Fertilizer</span>
            </div>
            {accordionEnabled ? (
              <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            ) : (
              <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            )}
          </div>
        </CardHeader>
        <CardContent
          className={`tw-py-3.5 tw-px-3.5 tw-space-y-4 ${accordionEnabled ? "" : "tw-hidden"}`}
        >
          <Form {...form}>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-[1fr_1fr_auto] tw-items-start tw-gap-4">
              <FormSelect
                control={control}
                name="fertilizer"
                label="Fertilizer Name"
                placeholder="Select Fertilizer Name"
                options={fertilizerDropdownList}
                resetKey={formEntryKey}
                required
                errorMessage="Fertilizer name is required"
              />
              <FormInputNumber
                control={control}
                label="kg/ha"
                placeholder="kg/ha"
                name="fertilizerQuantity"
                required
                errorMessage="Quantity is required"
                rules={{
                  validate: {
                    valueCheck: (v: any) => validateCultivationNumber(v)
                  }
                }}
              />
              <div className="tw-space-y-2 tw-mt-2">
                <div
                  className="tw-text-sm tw-font-medium tw-leading-none tw-text-foreground tw-opacity-0 tw-select-none"
                  aria-hidden
                >
                  Add
                </div>
                <TooltipProvider>
                {isEditingEntry ? (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="lg"
                          className="!tw-h-10 !tw-w-10 !tw-px-0"
                          onClick={addFertilizer}
                          disabled={lockStatus}
                          aria-label="Update"
                        >
                          <Check className="tw-h-4 tw-w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="tw-text-xs">Update</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="lg"
                          variant="outline"
                          className="!tw-h-10 !tw-w-10 !tw-px-0"
                          onClick={resetFertilizerForm}
                          disabled={lockStatus}
                          aria-label="Cancel"
                        >
                          <X className="tw-h-4 tw-w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="tw-text-xs">Cancel</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                ) : hasErrors ? (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="lg"
                          className="!tw-h-10 !tw-w-10 !tw-px-0"
                          onClick={addFertilizer}
                          disabled={lockStatus}
                          aria-label="Add"
                        >
                          <Plus className="tw-h-4 tw-w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="tw-text-xs">Add</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="lg"
                          variant="outline"
                          className="!tw-h-10 !tw-w-10 !tw-px-0"
                          onClick={resetFertilizerForm}
                          disabled={lockStatus}
                          aria-label="Clear"
                        >
                          <X className="tw-h-4 tw-w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="tw-text-xs">Clear</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        size="lg"
                        className="!tw-h-10 !tw-w-10 !tw-px-0"
                        onClick={addFertilizer}
                        disabled={lockStatus}
                      >
                        <Plus className="tw-h-4 tw-w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="tw-text-xs">Add</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                </TooltipProvider>
              </div>
            </div>
          </Form>
          {fertilizerTableList?.length > 0 && (
            <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="tw-border-b tw-border-border hover:tw-bg-transparent">
                    <TableHead
                      className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                      style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                    >
                      Fertilizer Name
                    </TableHead>
                    <TableHead
                      className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground"
                      style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                    >
                      kg/ha
                    </TableHead>
                    {!lockStatus && (
                      <TableHead
                        className="tw-px-4 tw-py-3 tw-text-xs tw-font-semibold tw-text-foreground tw-text-right tw-w-24"
                        style={{ backgroundColor: "var(--tableHeaderColor, #C9DDBD)" }}
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fertilizerTableList.map((item: any, index: any) => (
                    <TableRow
                      key={`fertilizer_${item.id ?? index}`}
                      className="hover:tw-bg-muted/20"
                    >
                      <TableCell className="tw-px-4 tw-py-3 tw-text-sm">
                        {item?.fertilizer_name ?? ""}
                      </TableCell>
                      <TableCell className="tw-px-4 tw-py-3 tw-text-sm tw-font-mono tw-text-muted-foreground">
                        {item?.fertilizerQuantity ?? ""}
                      </TableCell>
                      {!lockStatus && (
                        <TableCell className="tw-px-4 tw-py-3">
                          <div className="tw-flex tw-items-center tw-justify-end tw-gap-0.5">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="tw-h-7 tw-w-7 tw-p-0"
                              onClick={() => editFertilizer(item)}
                            >
                              <Pencil className="tw-h-3.5 tw-w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="tw-h-7 tw-w-7 tw-p-0 tw-text-destructive hover:tw-text-destructive"
                              onClick={() => deleteFertilizerData(item, index)}
                            >
                              <Trash2 className="tw-h-3.5 tw-w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <CultivarSaveReminder section="fertilizer" hidden={lockStatus} />
          <ConfirmModal
            viewModal={visibleDeleteModal}
            closeModal={() => setVisibleDeleteModal(false)}
            cancel={() => {
              setVisibleDeleteModal(false);
              setDeleteValue({});
            }}
            approve={deleteFertilizer}
            title={`Delete ${deleteValue.type}`}
            subTitle={DELETE_CONFIRM_MESSAGES.fertilizer}
          />
        </CardContent>
      </Card>
    ) : null;
  }
);

export default Fertilizer;
